import { Link } from "react-router-dom";
import { Person } from "@/components/patterns";
import type { LeadAssignmentHistoryItem, LeadInboxItem, LeadRecord } from "@/types";
import type { Task } from "@/types/task";
import { dateLocale, withClock } from "@/lib/dateTime";
import { LeadDuplicates } from "@/components/leads/LeadDuplicates";
import { SamplesCard } from "@/pages/samples/SamplesCard";

const Card = ({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) => (
  <section className="flex flex-col gap-2 rounded-lg border border-border bg-card px-3.5 py-3">
    <div className="flex items-center justify-between">
      <h2 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      {aside}
    </div>
    {children}
  </section>
);

const day = (d: string) => new Date(d).toLocaleDateString(dateLocale(), { day: "numeric", month: "short" });
const full = (u?: { first_name: string; last_name?: string } | null) => (u ? `${u.first_name} ${u.last_name || ""}`.trim() : "");

/** Owner, what is due next, the lead's enquiries, an account that may already exist, and who held the lead before */
export function LeadRelated({
  lead,
  tasks,
  inquiries,
  history,
  matches,
  canAssign,
  canTask,
  canMerge,
  onMerged,
  onReassign,
  onTask,
  onDoneTask,
}: {
  lead: LeadRecord;
  tasks: Task[];
  inquiries: LeadInboxItem[];
  history: LeadAssignmentHistoryItem[];
  matches: { id: string; name: string; owner: string | null }[];
  canAssign: boolean;
  canTask: boolean;
  canMerge: boolean;
  onMerged: (keptId: string) => void;
  onReassign: () => void;
  onTask: () => void;
  onDoneTask: (t: Task) => void;
}) {
  const open = tasks.filter((t) => t.task_status === "OPEN").sort((a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime());
  const owner = lead.owner ? full(lead.owner) : "";
  const converted = !!lead.converted_account_id;

  return (
    <div className="flex flex-col gap-3">
      <LeadDuplicates id={lead.id} canMerge={canMerge} onMerged={onMerged} />
      <SamplesCard leadId={lead.id} canRequest={!converted && lead.lead_status !== "DISQUALIFIED"} />
      <Card title="Owner" aside={canAssign && !converted ? <button type="button" onClick={onReassign} className="text-xs text-primary hover:underline">{owner ? "Reassign" : "Assign"}</button> : undefined}>
        {owner ? (
          <>
            <Person name={owner} />
            {lead.assigned_at && <span className="text-xs text-muted-foreground">Assigned {day(lead.assigned_at)}</span>}
          </>
        ) : (
          <span className="text-[13px] text-warning">In the unassigned pool</span>
        )}
      </Card>

      <Card title="Next follow-up">
        {open.length === 0 && !lead.next_follow_up_at && <span className="text-xs text-muted-foreground">Nothing scheduled.</span>}
        {open.slice(0, 3).map((t) => (
          <label key={t.id} className="flex items-center gap-2.5 rounded-lg border border-border px-2.5 py-2">
            <input type="checkbox" className="size-4 accent-primary" onChange={() => onDoneTask(t)} aria-label={`Mark "${t.title}" done`} />
            <span className="min-w-0 flex-1 truncate text-[13px]">{t.title}</span>
            <span className="font-mono text-xs text-primary">{new Date(t.due_at).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }))}</span>
          </label>
        ))}
        {open.length === 0 && lead.next_follow_up_at && <span className="text-[13px]">Follow up on {day(lead.next_follow_up_at)}</span>}
        {canTask && (
          <button type="button" onClick={onTask} className="text-left text-[13px] font-medium text-primary hover:underline">
            + Schedule follow-up
          </button>
        )}
      </Card>

      {inquiries.length > 0 && (
        <Card title="Enquiries" aside={<span className="font-mono text-xs text-muted-foreground">{inquiries.length}</span>}>
          {inquiries.slice(0, 4).map((q) => (
            <div key={q.id} className="flex flex-col">
              <span className="truncate text-[13px] font-medium">{String(q.normalized?.product_interest || q.normalized?.requirement_notes || "Enquiry").slice(0, 60)}</span>
              <span className="text-xs text-muted-foreground">
                {q.connection?.name || "Direct"} · {day(q.received_at)}
              </span>
            </div>
          ))}
        </Card>
      )}

      {matches.length > 0 && !converted && (
        <section className="flex flex-col gap-1.5 rounded-lg bg-warning-soft px-3.5 py-3">
          <h2 className="text-[11.5px] font-semibold uppercase tracking-wide text-warning">Possible duplicate</h2>
          <span className="text-[13px]">
            An account named <strong>{matches[0]!.name}</strong> already exists{matches[0]!.owner ? `, owned by ${matches[0]!.owner}` : ""}. When you convert, you can link to it instead of creating a new one.
          </span>
          <Link to={`/accounts/${matches[0]!.id}`} className="text-[13px] font-medium text-primary hover:underline">
            View account
          </Link>
        </section>
      )}

      {history.length > 0 && (
        <Card title="Assignment history">
          {history.slice(0, 5).map((h) => (
            <div key={h.id} className="text-[13px]">
              {full(h.to_user) || "Unassigned pool"} <span className="text-xs text-muted-foreground">· {day(h.created_on)} · {String(h.reason || "").replace(/_/g, " ").toLowerCase()}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
